import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { useCopy } from './copy/copyMode';
function FeatureItem({
  text
}: {
  text: string;
}) {
  return <li className="flex items-start gap-3 text-sm md:text-base text-[#2B211A]" data-id="element-1657">
      <div className="w-1.5 h-1.5 bg-[#2B211A] mt-2 shrink-0" data-id="element-1658" />
      <span className="leading-relaxed" data-id="element-1659">{text}</span>
    </li>;
}
export function PricingSection() {
  const copy = useCopy();
  return <section className="relative w-full bg-[#FAF8F4] py-16 md:py-32 overflow-hidden" data-id="element-1660">
      {/* Halftone dot-matrix texture — matches the other cream sections */}
      <div aria-hidden="true" className="absolute inset-0 dot-matrix opacity-40 pointer-events-none z-0" data-id="element-1661" />
      <div className="max-w-[1280px] mx-auto px-6 md:px-10 relative z-10" data-id="element-1662">
        {/* Header */}
        <div className="mb-12 md:mb-16" data-id="element-1663">
          <h2 className="text-3xl md:text-5xl font-title font-medium text-[#2B211A] tracking-tight leading-[1.1]" data-id="element-1664">
            {copy.pricingSection.headline}
          </h2>
        </div>

        {/* Grid Container */}
        <div className="relative border-t border-b border-dotted border-neutral-300" data-id="element-1665">
          {/* Vertical dotted lines for desktop grid */}
          <div className="hidden md:block absolute top-0 bottom-0 left-1/3 w-px border-l border-dotted border-neutral-300" data-id="element-1666" />
          <div className="hidden md:block absolute top-0 bottom-0 left-2/3 w-px border-l border-dotted border-neutral-300" data-id="element-1667" />
          <div className="hidden md:block absolute top-0 bottom-0 left-0 w-px border-l border-dotted border-neutral-300" data-id="element-1668" />
          <div className="hidden md:block absolute top-0 bottom-0 right-0 w-px border-l border-dotted border-neutral-300" data-id="element-1669" />

          <div className="grid grid-cols-1 md:grid-cols-3 relative z-10" data-id="element-1670">
            {/* Starter Tier — paid, opens with a 7-day free trial */}
            <div className="p-6 md:p-8 border-b border-dotted border-neutral-300 md:border-b-0 flex flex-col" data-id="element-1671">
              <div className="bg-[#F3EFEA] rounded-2xl p-6 md:p-8 mb-8 h-48 flex flex-col justify-between" data-id="element-1672">
                <h3 className="font-title font-normal text-2xl text-[#2B211A]" data-id="element-1673">
                  Starter
                </h3>
                <div data-id="element-1674">
                  <div className="text-neutral-500 text-lg" data-id="element-1675">
                    <span className="text-2xl text-[#2B211A]" data-id="element-1676">$49</span>
                    /month
                  </div>
                  <span className="inline-flex mt-2 items-center gap-1.5 text-xs font-semibold text-[#EA580C] bg-[#FBEAE0] px-2.5 py-1 rounded-full" data-id="element-1677">
                    7-day free trial
                  </span>
                </div>
              </div>
              <ul className="space-y-3 mb-10 flex-1" data-id="element-1678">
                <FeatureItem text="1 workspace, unlimited members" data-id="element-1679" />
                <FeatureItem text="Starter monthly credit allowance" data-id="element-1680" />
                <FeatureItem text="Slack-native agent in threads + mentions" data-id="element-1681" />
                <FeatureItem text="Integrations + tool execution" data-id="element-1682" />
                <FeatureItem text="Scheduled tasks & crons" data-id="element-1683" />
                <FeatureItem text="Drafts + artifacts where supported" data-id="element-1684" />
              </ul>
              <Link to="/signup" className="w-full flex justify-center bg-[#2B211A] hover:bg-[#1C1510] text-white py-3 rounded-xl font-medium transition-colors" data-id="element-1685">
                Start 7-day trial
              </Link>
            </div>

            {/* Pro Tier — paid, opens with a 7-day free trial */}
            <div className="p-6 md:p-8 border-b border-dotted border-neutral-300 md:border-b-0 flex flex-col" data-id="element-1686">
              <div className="bg-[#F3EFEA] rounded-2xl p-6 md:p-8 mb-8 h-48 flex flex-col justify-between" data-id="element-1687">
                <h3 className="font-title font-normal text-2xl text-[#2B211A]" data-id="element-1688">
                  Pro
                </h3>
                <div data-id="element-1689">
                  <div className="text-neutral-500 text-lg" data-id="element-1690">
                    <span className="text-2xl text-[#2B211A]" data-id="element-1691">$199</span>
                    /month
                  </div>
                  <span className="inline-flex mt-2 items-center gap-1.5 text-xs font-semibold text-[#EA580C] bg-[#FBEAE0] px-2.5 py-1 rounded-full" data-id="element-1692">
                    7-day free trial
                  </span>
                </div>
              </div>
              <ul className="space-y-3 mb-10 flex-1" data-id="element-1693">
                <FeatureItem text="Multiple workspaces, unlimited members" data-id="element-1694" />
                <FeatureItem text="Higher monthly credit allowance" data-id="element-1695" />
                <FeatureItem text="Slack-native agent in threads + mentions" data-id="element-1696" />
                <FeatureItem text="Integrations + tool execution" data-id="element-1697" />
                <FeatureItem text="Scheduled tasks & crons" data-id="element-1698" />
                <FeatureItem text="Drafts + artifacts where supported" data-id="element-1699" />
              </ul>
              <Link to="/signup" className="w-full flex justify-center bg-[#EA580C] hover:bg-[#C2410C] text-white py-3 rounded-xl font-medium transition-colors" data-id="element-1700">
                Start 7-day trial
              </Link>
            </div>

            {/* Custom Tier — enterprise, contact sales */}
            <div className="p-6 md:p-8 flex flex-col" data-id="element-1701">
              <div className="bg-[#F3EFEA] rounded-2xl p-6 md:p-8 mb-8 h-48 flex flex-col justify-between" data-id="element-1702">
                <div data-id="element-1703">
                  <span className="block text-[11px] font-bold tracking-[2px] uppercase text-[#EA580C] mb-2" data-id="element-1704">
                    Enterprise
                  </span>
                  <h3 className="font-title font-normal text-2xl text-[#2B211A]" data-id="element-1705">
                    Custom
                  </h3>
                </div>
                <div className="text-neutral-500 text-lg" data-id="element-1706">Flexible pricing</div>
              </div>
              <ul className="space-y-3 mb-10 flex-1" data-id="element-1707">
                <FeatureItem text="Everything in Pro" data-id="element-1708" />
                <FeatureItem text="Invoicing + custom billing terms" data-id="element-1709" />
                <FeatureItem text="Security review support + DPA" data-id="element-1710" />
                <FeatureItem text="SLA + priority support" data-id="element-1711" />
                <FeatureItem text="Dedicated onboarding + tailored limits/controls" data-id="element-1712" />
              </ul>
              <Link to="/contact" className="w-full flex justify-center bg-[#2B211A] hover:bg-[#1C1510] text-white py-3 rounded-xl font-medium transition-colors" data-id="element-1713">
                Contact sales
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>;
}